import React, { Component } from 'react';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { CheckCircleIcon, ArrowLeftIcon, DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import CatastropheWelcome from './CatastropheWelcome';
import CatastropheSummaryPage from './CatastropheSummaryPage';
import CatastropheQuestionPanel from './CatastropheQuestionPanel';
import CatastropheDocumentPreview from './CatastropheDocumentPreview';
import CatastropheProgressBar from './CatastropheProgressBar';
import { catastrophicQuestionnaireSchema } from './catastrophic-questionnaire-schema';
import { generateCatastrophicDocument } from './catastrophic-template-generator';
import generateCatastrophicPlanDocument from './catastrophic-plan-generator';
import SuccessionPageLayout from './SuccessionPageLayout';

const SAVE_CATASTROPHE_DATA = gql`
  mutation SaveCatastropheData($formData: String!, $currentSection: Int!, $sectionCount: Int!, $status: String!, $welcomeShown: Boolean!) {
    saveCatastropheData(
      formData: $formData,
      currentSection: $currentSection,
      sectionCount: $sectionCount,
      status: $status,
      welcomeShown: $welcomeShown
    ) {
      catastropheFormDatum {
        id
        formData
        currentSection
        sectionCount
        status
        welcomeShown
      }
      errors
    }
  }
`;

const FINALIZE_CATASTROPHE = gql`
  mutation FinalizeCatastrophe {
    finalizeCatastrophe {
      catastropheFormDatum {
        id
        status
        aiInsights {
          keyStrengths
          identifiedRisks
          actionRecommendations
          complianceStatus
        }
      }
      errors
    }
  }
`;

class CatastropheGenerator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSection: 0,
      formData: {},
      previewContent: '',
      loading: false,
      error: null,
      showSummary: false,
      welcomeShown: false,
      aiInsights: null
    };
  }

  componentDidMount() {
    const { catastropheData } = this.props;
    if (catastropheData) {
      this.setState({
        currentSection: catastropheData.currentSection || 0,
        formData: JSON.parse(catastropheData.formData || '{}'),
        welcomeShown: catastropheData.welcomeShown || false,
        aiInsights: catastropheData.aiInsights || null
      });
      
      if (catastropheData.status === 'completed') {
        this.setState({ showSummary: true });
        this.updatePreview(JSON.parse(catastropheData.formData || '{}'));
      }
    }
  }

  updatePreview = (formData) => {
    // Only update preview if we have form data
    if (formData && Object.keys(formData).length > 0) {
      const content = generateCatastrophicPlanDocument(formData);
      this.setState({ previewContent: content });
    } else {
      // Set a default message for empty form data
      this.setState({ 
        previewContent: '# Business Catastrophe Plan\n\nStart filling out the questionnaire to generate your Business Catastrophe Plan document.' 
      });
    }
  };

  handleFormDataChange = (newFormData) => {
    const { currentSection, welcomeShown } = this.state;
    this.setState({ formData: newFormData });
    
    this.updatePreview(newFormData);
    
    return this.saveCatastropheFormData({
      variables: {
        formData: JSON.stringify(newFormData),
        currentSection,
        sectionCount: catastrophicQuestionnaireSchema.sections.length,
        status: 'in_progress',
        welcomeShown
      }
    }).then(({ data }) => {
      if (data?.saveCatastropheData?.errors?.length > 0) {
        this.setState({ error: data.saveCatastropheData.errors[0] });
      }
    });
  };

  handleSectionChange = (direction) => {
    const { currentSection, formData, welcomeShown } = this.state;
    const newSection = direction === 'next' 
      ? Math.min(currentSection + 1, catastrophicQuestionnaireSchema.sections.length - 1)
      : Math.max(currentSection - 1, 0);
    
    this.setState({ currentSection: newSection });
    
    return this.saveCatastropheFormData({
      variables: {
        formData: JSON.stringify(formData),
        currentSection: newSection,
        sectionCount: catastrophicQuestionnaireSchema.sections.length,
        status: 'in_progress',
        welcomeShown
      }
    }).then(({ data }) => {
      if (data?.saveCatastropheData?.errors?.length > 0) {
        this.setState({ error: data.saveCatastropheData.errors[0] });
      }
    });
  };

  handleFinalizeClick = async () => {
    this.setState({ loading: true });
    try {
      const { data } = await this.finalizeCatastrophe();
      console.log("Finalize response:", data);
      
      if (data?.finalizeCatastrophe?.errors?.length > 0) {
        this.setState({ 
          error: data.finalizeCatastrophe.errors[0],
          loading: false
        });
        return;
      }
      
      // Update state with the response data and show summary
      if (data?.finalizeCatastrophe?.catastropheFormDatum) {
        const updatedFormData = this.state.formData;
        
        this.setState({
          formData: updatedFormData,
          showSummary: true,
          catastropheData: data.finalizeCatastrophe.catastropheFormDatum,
          loading: false,
          aiInsights: data.finalizeCatastrophe.catastropheFormDatum.aiInsights
        }, () => {
          console.log("State updated:", this.state);
          this.updatePreview(updatedFormData);
        });
      } else {
        this.setState({ 
          error: "Failed to finalize plan",
          loading: false
        });
      }
    } catch (err) {
      console.error("Finalize error:", err);
      this.setState({ 
        error: err.message,
        loading: false
      });
    }
  };

  handleBackToEditor = () => {
    this.setState({ showSummary: false });
  };

  handleStartCatastrophe = async () => {
    const { formData } = this.state;
    this.setState({ welcomeShown: true });
    
    try {
      const { data } = await this.saveCatastropheFormData({
        variables: {
          formData: JSON.stringify(formData),
          currentSection: 0,
          sectionCount: catastrophicQuestionnaireSchema.sections.length,
          status: 'in_progress',
          welcomeShown: true
        }
      });
      if (data?.saveCatastropheData?.errors?.length > 0) {
        this.setState({ error: data.saveCatastropheData.errors[0] });
      }
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  // Helper methods for SuccessionPageLayout
  getPageTitle = () => {
    const { welcomeShown, showSummary } = this.state;
    if (!welcomeShown) {
      return "Business Catastrophe Planning";
    }
    if (showSummary) {
      return "Catastrophe Plan Summary";
    }
    return "Business Catastrophe Plan Generator";
  };

  getPageSubtitle = () => {
    const { welcomeShown, showSummary } = this.state;
    if (!welcomeShown) {
      return "Protect your clients and practice in case of unexpected events";
    }
    if (showSummary) {
      return "Review your completed business catastrophe plan and insights";
    }
    return "Complete your business catastrophe plan questionnaire";
  };

  getActionButtons = () => {
    const { welcomeShown, showSummary } = this.state;
    const { catastropheData } = this.props;
    const isCompleted = catastropheData?.status === 'completed';

    if (!welcomeShown) {
      return []; // No buttons on welcome screen
    }

    if (showSummary) {
      return [
        {
          text: "Edit Plan",
          onClick: this.handleBackToEditor,
          icon: "edit",
          variant: "default"
        },
        {
          text: "Download PDF",
          onClick: () => window.location.href = '/succession/catastrophe/download',
          icon: "export",
          variant: "primary"
        }
      ];
    }

    // Questionnaire view
    const buttons = [];
    
    if (isCompleted) {
      buttons.push({
        text: "View Summary",
        onClick: this.handleFinalizeClick,
        variant: "primary"
      });
    }

    return buttons;
  };

  render() {
    const { catastropheData } = this.props;
    const { currentSection, formData, previewContent, loading, error, showSummary, welcomeShown, aiInsights } = this.state;
    const { sections } = catastrophicQuestionnaireSchema;
    const isCompleted = catastropheData?.status === 'completed';
    
    // If showing summary, render the summary view
    if (showSummary) {
      // Convert camelCase aiInsights to snake_case for the CatastropheSummaryPage component
      const formattedAiInsights = aiInsights ? {
        key_strengths: aiInsights.keyStrengths,
        identified_risks: aiInsights.identifiedRisks,
        action_recommendations: aiInsights.actionRecommendations,
        compliance_status: aiInsights.complianceStatus
      } : null;
      
      return (
        <CatastropheSummaryPage
          formData={formData}
          previewContent={previewContent}
          onRevise={this.handleBackToEditor}
          aiInsights={formattedAiInsights}
        />
      );
    }
    
    // Always wrap the content in the Mutation components
    return (
      <Mutation mutation={SAVE_CATASTROPHE_DATA}>
        {(saveCatastropheFormData, { loading: saving }) => {
          this.saveCatastropheFormData = saveCatastropheFormData;
          
          return (
            <Mutation mutation={FINALIZE_CATASTROPHE}>
              {(finalizeCatastrophe) => {
                this.finalizeCatastrophe = finalizeCatastrophe;
                
                // If welcome hasn't been shown, display the welcome screen
                if (!welcomeShown) {
                  return (
                    <SuccessionPageLayout
                      title={this.getPageTitle()}
                      subtitle={this.getPageSubtitle()}
                      actionButtons={this.getActionButtons()}
                    >
                      <div className="max-w-4xl mx-auto">
                        {error && (
                          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: 'var(--succession-error-bg)', border: '1px solid var(--succession-error-border)' }}>
                            <div className="flex">
                              <div className="ml-3">
                                <h3 className="text-sm font-medium" style={{ color: 'var(--succession-error)' }}>Error saving data</h3>
                                <div className="mt-2 text-sm" style={{ color: 'var(--succession-error)' }}>
                                  <p>{error}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <CatastropheWelcome onStart={this.handleStartCatastrophe} />
                      </div>
                    </SuccessionPageLayout>
                  );
                }
                
                return (
                  <SuccessionPageLayout
                    title={this.getPageTitle()}
                    subtitle={this.getPageSubtitle()}
                    actionButtons={this.getActionButtons()}
                  >
                    <div className="max-w-7xl mx-auto space-y-6">
                      {isCompleted && (
                        <div className="flex items-center py-2 px-4 rounded-md" style={{ backgroundColor: 'var(--succession-info-bg)', border: '1px solid var(--succession-info-border)' }}>
                          <span className="text-sm" style={{ color: 'var(--succession-info)' }}>
                            Your Business Catastrophe Plan is already complete. You can make edits here or{' '}
                            <button 
                              type="button" 
                              className="underline font-medium" 
                              style={{ color: 'var(--succession-primary)' }}
                              onClick={this.handleFinalizeClick}
                            >
                              return to the summary page
                            </button>.
                          </span>
                        </div>
                      )}
                      
                      {error && (
                        <div className="p-4 rounded-md" style={{ backgroundColor: 'var(--succession-error-bg)', border: '1px solid var(--succession-error-border)' }}>
                          <div className="flex">
                            <div className="ml-3">
                              <h3 className="text-sm font-medium" style={{ color: 'var(--succession-error)' }}>Error saving data</h3>
                              <div className="mt-2 text-sm" style={{ color: 'var(--succession-error)' }}>
                                <p>{error}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                            
                            <CatastropheProgressBar 
                              currentSection={currentSection}
                              totalSections={sections.length}
                              sectionTitles={sections.map(s => s.title)}
                            />
                            
                            {isCompleted && (
                              <div className="flex justify-end items-center mt-4 mb-2 py-2 px-3 rounded-md" style={{ backgroundColor: 'var(--succession-success-bg)', border: '1px solid var(--succession-success-border)' }}>
                                <CheckCircleIcon className="h-5 w-5 mr-2" style={{ color: 'var(--succession-success)' }} />
                                <span className="text-sm font-medium mr-4" style={{ color: 'var(--succession-success)' }}>Your Business Catastrophe Plan is complete!</span>
                                <button
                                  type="button"
                                  onClick={this.handleFinalizeClick}
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                                  style={{ backgroundColor: 'var(--succession-success)', focusRingColor: 'var(--succession-success)' }}
                                >
                                  Finalize Plan
                                </button>
                              </div>
                            )}
                            
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-8">
                              <div className="shadow rounded-lg p-6" style={{ backgroundColor: 'var(--succession-bg-primary)', border: '1px solid var(--succession-border)' }}>
                                <CatastropheQuestionPanel
                                  section={sections[currentSection]}
                                  formData={formData}
                                  onFormDataChange={this.handleFormDataChange}
                                  onNext={() => this.handleSectionChange('next')}
                                  onPrevious={() => this.handleSectionChange('previous')}
                                  isFirstSection={currentSection === 0}
                                  isLastSection={currentSection === sections.length - 1}
                                />
                              </div>
                              
                              <div className="shadow rounded-lg p-6" style={{ backgroundColor: 'var(--succession-bg-primary)', border: '1px solid var(--succession-border)' }}>
                                <CatastropheDocumentPreview content={previewContent} />
                              </div>
                            </div>
                            
                      {(loading || saving) && (
                        <div className="fixed bottom-4 right-4 py-2 px-4 rounded-md shadow" style={{ backgroundColor: 'var(--succession-primary)', color: 'white' }}>
                          Saving...
                        </div>
                      )}
                    </div>
                  </SuccessionPageLayout>
                );
              }}
            </Mutation>
          );
        }}
      </Mutation>
    );
  }
}

export default CatastropheGenerator; 